import { Listbox } from '@headlessui/react';
import { type Dispatch, type MouseEvent, type SetStateAction } from 'react';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/sz/arrow-down.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/sz/trash-icon.svg';
import { useTranslate } from 'hooks';
import type { TranslationKey } from 'locales/flattenMessages';
import { renderIcon, twMergeClsx } from 'utils';

type CardOptionType = {
  crypt?: string;
  maskedPan: string;
  bankBin?: string;
  newCard?: boolean;
  id: number;
};

type Props = {
  cards: CardOptionType[];
  selectedCard: CardOptionType;
  handleDelete: (value: string) => void;
  setSelectedCard: Dispatch<SetStateAction<CardOptionType>>;
  label?: string;
};

const SelectCard = ({ selectedCard, cards, handleDelete, setSelectedCard, label }: Props) => {
  const { t } = useTranslate();

  const defaulValue = { maskedPan: 'fonbet.new-bank-card', newCard: true };
  const renderBtnContent = () => {
    if (selectedCard) {
      if (selectedCard.newCard) {
        return <span className="flex-grow">{t(selectedCard.maskedPan as TranslationKey)}</span>;
      }

      return selectedCard.bankBin ? (
        <>
          {renderIcon(selectedCard.bankBin)}
          <span className="flex-grow">{selectedCard.maskedPan}</span>
        </>
      ) : null;
    }

    return null;
  };

  const deleteCard = (e: MouseEvent<HTMLButtonElement>, pan: string) => {
    handleDelete(pan);
    e.stopPropagation();
  };

  return (
    <Listbox as="div" className="relative" value={selectedCard} onChange={setSelectedCard}>
      {({ open }) => (
        <>
          {label && (
            <Listbox.Label
              className={twMergeClsx(
                'text-sm leading-[18px] font-medium mb-1.5 block whitespace-nowrap text-fonbet-gray-200',
                open && 'text-fonbet-blue-200',
              )}
            >
              {label}
            </Listbox.Label>
          )}
          <Listbox.Button
            className={twMergeClsx(
              'z-10 flex items-center gap-[10px] relative px-4 py-[6px] md:py-3 w-full text-left bg-white outline-none h-[42px] md:h-[44px] rounded-xsmall text-base text-black leading-[30px] border border-solid border-fonbet-gray-100',
              open && 'border-fonbet-blue-200',
            )}
          >
            {renderBtnContent()}
            <ArrowDownIcon
              className={twMergeClsx(
                'w-5 h-5 transition-transform fill-fonbet-gray-400/50 -mr-3',
                open && 'rotate-180 fill-fonbet-gray-400',
              )}
            />
          </Listbox.Button>
          <Listbox.Options className="absolute border border-solid border-fonbet-blue-200 w-full z-50 rounded-xsmall mt-1 text-base text-black overflow-hidden">
            <Listbox.Option
              className="px-4 py-[6px] h-[42px] md:h-[50px] md:py-3 border-b border-fonbet-gray-100 bg-white last:border-none cursor-pointer transition-colors"
              value={defaulValue}
            >
              {t(defaulValue.maskedPan as TranslationKey)}
            </Listbox.Option>
            {cards?.map((item) => (
              <Listbox.Option
                className="px-4 py-[6px] h-[42px] md:h-[50px] md:py-3 border-b border-fonbet-gray-100 bg-white last:border-none cursor-pointer transition-colors flex items-center gap-[10px]"
                value={item}
                key={item.maskedPan}
              >
                {item.bankBin && renderIcon(item.bankBin)}
                <span className="flex-grow">{item.maskedPan}</span>
                <button
                  className="w-6 h-6"
                  type="button"
                  onClick={(e) => deleteCard(e, item.maskedPan)}
                >
                  <TrashIcon className="w-[18px] h-[18px] fill-fonbet-gray-400/50 hover:fill-fonbet-gray-400" />
                </button>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </>
      )}
    </Listbox>
  );
};

export { SelectCard };
